<template>
  <loader
    v-if="isLoading"
    style="height: 100vh;"/>
  <div
    v-else-if="!dataIsFetched"
    class="ui small placeholder segment">
    <div class="ui icon disabled header">
      <i class="warning circle icon"/>
      {{ $t(`patients.coaching.errorFetchingCoachingContents`) }}
    </div>
    <div class="inline">
      <button
        @click="getCoachingContentsAndPlans"
        class="ui primary button">
        Retry
      </button>
    </div>
  </div>
  <segment v-else>
    <h3 class="ui dividing header">
      {{ $tc('action', 2) }}
      <div class="sub header">
        {{ $t('patients.coaching.actionSub') }}
      </div>
    </h3>

    <action-table
      @edit-selected-draft-content="editSelectedDraftContent"
      @show-deactivate-selected-added-content="showDeactivateSelectedAddedContentModal"
      @delete-one-draft-content="deleteOneDraftContent"
      :added-contents="addedContents.action"
      :draft-contents="draftContents.action"/>

    <div class="horizontal-margin modules-container">
      <action-addition-module
        @add-new-items="addDraftContents"
        :medgate-id="patient.hasOwnProperty('externalId') ? patient.externalId : patient.data.medgateId"
        :patient-objectives="patientObjectives"
        :patient-notes="patient.data.coachNotes"
        :added-item-ids="addedContentIds.action"
        :new-items="newContents.action"/>

      <action-plan-addition-module
        @add-new-items="addDraftContentsViaPlans"
        :medgate-id="patient.hasOwnProperty('externalId') ? patient.externalId : patient.data.medgateId"
        :patient-objectives="patientObjectives"
        :patient-notes="patient.data.coachNotes"
        :added-item-ids="addedContentIds.action"
        :new-items="newPlans.action"/>

      <action-edit-modal
        ref="editContentModal"
        :content="selectedDraftContent"
        @update-selected-draft-content="updateSelectedDraftContent"/>

      <action-deactivation-modal
        ref="deactivateContentModal"
        :status="contentDeactivationStatus"
        :content="selectedAddedContent"
        @deactivate-selected-added-content="deactivateSelectedAddedContent"/>
    </div>

    <h3 class="ui dividing header">
      {{ $tc('lesson', 2) }}
      <div class="sub header">
        {{ $t('patients.coaching.lessonSub') }}
      </div>
    </h3>

    <lesson-table
      :added-contents="addedContents.lesson"
      :draft-contents="draftContents.lesson"
      @delete-one-draft-content="deleteOneDraftContent"/>

    <div class="horizontal-margin modules-container">
      <lesson-addition-module
        @add-new-items="addDraftContents"
        :medgate-id="patient.hasOwnProperty('externalId') ? patient.externalId : patient.data.medgateId"
        :patient-objectives="patientObjectives"
        :patient-notes="patient.data.coachNotes"
        :added-item-ids="addedContentIds.lesson"
        :new-items="newContents.lesson"/>

      <lesson-plan-addition-module
        @add-new-items="addDraftContentsViaPlans"
        :medgate-id="patient.hasOwnProperty('externalId') ? patient.externalId : patient.data.medgateId"
        :patient-objectives="patientObjectives"
        :patient-notes="patient.data.coachNotes"
        :added-item-ids="addedContentIds.lesson"
        :new-items="newPlans.lesson"/>
    </div>

    <div class="horizontal-margin">
      <div
        class="ui big primary button"
        @click.prevent="updateCoachingContents"
        :class="{ disabled: updatingCoachingContents}">
        <span v-if="!updatingCoachingContents"><i class="save icon"/>Update Coaching Contents</span>
        <span v-else>Updating Coaching Contents&nbsp;&nbsp;<i class="ui loading spinner icon"/></span>
      </div>
    </div>

    <div class="horizontal-margin">
      <message :message="updateMessage"/>
    </div>
  </segment>
</template>

<script>
import { Lesson, UserLesson, DraftUserLesson, Action, UserAction, DraftUserAction, ActionReminder , Patient } from '../../structs/structs'
import { DailyTrackableConstructor, OneTimeTrackableConstructor, NonTrackableConstructor, APIError } from '../../helpers'
import { ActionTable, LessonTable, ActionAdditionModule, LessonAdditionModule, ActionPlanAdditionModule, LessonPlanAdditionModule, ActionEditModal, ActionDeactivationModal } from '../extended'
import Segment from '../utility/Segment'

const structs = new Map(
  [
    ['program-lesson', Lesson],
    ['program-action', Action],
    ['user-lesson', UserLesson],
    ['user-action', UserAction],
    ['draft-user-lesson', DraftUserLesson],
    ['draft-user-action', DraftUserAction]
  ]
)
const contentTypes = ['action', 'lesson']
const dataTypes = ['draftContents', 'contents', 'plans']

/*
  NOTE: Content Addition Logic
  0. Retrieve list of contents
  0. Retrieve added contents of user
  1. Infer new contents from `tentativeContents`
  2. Select new contents to add
  3. Clone contents into `draftContents`
  4. Derive added contents from `tentativeContents`
*/

/*
  # NOTE: Plan Addition Logic
  0. Retrieve list of program plans
  1. Derive added plans from `tentativeContents`
  2. Infer new plans from `tentativeContents`
  3. Select new plans to add
  4. Decompose plan into contents
  5. Clone contents into `draftContents`
*/

export default {
  name: 'CoachingContents',
  components: { ActionDeactivationModal, ActionEditModal, ActionAdditionModule, LessonAdditionModule, ActionPlanAdditionModule, LessonPlanAdditionModule, Segment, ActionTable, LessonTable },
  props: {
    patientId: {
      type: Number,
      required: true
    },
    patient: {
      type: Patient,
      required: true
    }
  },
  data () {
    return {
      isLoading: true,
      updatingCoachingContents: false,
      updateMessage: { class: '', text: '' },
      contentDeactivationStatus: 'ready',
      selectedDraftContent: new DraftUserAction(),
      selectedAddedContent: new UserAction(),
      contents: {
        action: null,
        lesson: null
      },
      plans: {
        action: null,
        lesson: null
      },
      addedContents: {
        action: null,
        lesson: null
      },
      draftContents: {
        action: [],
        lesson: []
      }
    }
  },
  computed: {
    addedContentIds () {
      return this.mapUserItemIds(this.addedContents)
    },
    // NOTE: IDs of new contents from draft contents to be added to user
    draftContentIds () {
      return this.mapUserItemIds(this.draftContents)
    },
    // NOTE: For dropdown options in content addition module
    newContents () {
      return Object.fromEntries(
        contentTypes
          .map(type => (
            [
              type,
              this.contents[type]
                .filter(content => !this.tentativeContentIds[type].includes(content.id))
            ]
          ))
      )
    },
    // NOTE: For dropdown options in content addition module
    newPlans () {
      return Object.fromEntries(
        contentTypes.map(type => (
          [
            type,
            this.plans[type]
              .filter(plan => (
                !plan.data.childPlans.every(contentId => this.tentativeContentIds.action.includes(contentId))
              ))
          ]
        ))
      )
    },
    // NOTE: IDs of all added and draft contents
    tentativeContentIds () {
      return Object.fromEntries(
        contentTypes
          .map(type => (
            [
              type,
              Array.from(
                new Set(
                  [
                    ...this.addedContentIds[type],
                    ...this.draftContentIds[type]
                  ]
                )
              )
            ]
          ))
      )
    },
    // NOTE: Request body to create user events from action contents
    draftUserEvents () {
      return this.draftContents.action.flatMap(
        action => {
          let actionId = action.data.id
          let coachingDayOneTimestamp = this.patient.data.startDate
          let startDay = action.data.options.startDay
          let endDay = action.data.options.endDay

          let fields = { actionId, coachingDayOneTimestamp, startDay, endDay }

          let tracking = action.data.options.userTracking

          return (
            (tracking.status && tracking.isDaily && new DailyTrackableConstructor(fields).events) ||
            (tracking.status && [new OneTimeTrackableConstructor(fields).event]) ||
            (new NonTrackableConstructor(fields).event)
          )
        }
      )
    },
    dataIsFetched () {
      return dataTypes.every(key => Object.values(this[key]).every(type => !!type))
    },
    patientObjectives () {
      if (!this.patient.data.objectives.length) { return 'Not selected' }
      return this.patient.data.objectives.join(', ')
    }
  },
  mounted () {
    this.getCoachingContentsAndPlans()
      .catch(
        error => {
          this.$rollbar.error(error.message, error.response)
        }
      )
      .finally(
        () => { this.isLoading = false }
      )
  },
  methods: {
    async getCoachingContentsAndPlans () {
      let userPlans, coachingContents, coachingPlans

      try {
        userPlans = await this.getAddedContents()
      } catch (error) {
        this.$rollbar.error(error)
        userPlans = await this.fetchAddedContents()
      }

      coachingContents = await this.getCoachingContents()
      coachingPlans = await this.getCoachingPlans()

      contentTypes.forEach(
        type => {
          this.addedContents[type] = (
            userPlans
              .filter(userPlan => userPlan.planType === type)
              .map(userPlan => new (structs.get(`user-${type}`))(userPlan))
          )
        }
      )

      contentTypes.forEach(
        type => {
          this.contents[type] = (
            coachingContents
              .filter(content => content.planType === type && !!content.data && !content.deletedStatus)
              .map(content => new (structs.get(`program-${type}`))(content))
          )
        }
      )

      contentTypes.forEach(
        type => {
          this.plans[type] = (
            coachingPlans
              .filter(
                plan => (
                  plan.planType === type &&
                  !plan.data.deletedStatus &&
                  plan.data.childPlans.length
                )
              )
          )
        }
      )
    },
    getAddedContents () {
      return new Promise(
        (resolve, reject) => {
          let userPlans = this.patient && this.patient.hasOwnProperty('userPlans') && Array.isArray(this.patient.userPlans) && this.patient.userPlans

          return userPlans ? resolve(userPlans) : reject(new Error('Coaching content not found in program user'))
        }
      )
    },
    fetchAddedContents () {
      return new Promise(
        (resolve, reject) => {
          let requestUrl = process.env.VUE_APP_PROGRAM_URL + '/programUsers/' + this.patient.userId + '/userPlans'

          this.$axios.get(
            requestUrl,
            this.requestConfig
          ).then(
            ({ data }) => {
              resolve(data.result)
            },
            ({ response }) => {
              reject(new APIError('Error fetching user plans for program user', response))
            }
          )
        }
      )
    },
    getCoachingPlans () {
      return new Promise(
        (resolve, reject) => {
          let requestUrl = process.env.VUE_APP_PROGRAM_URL + '/programPlanTemplates?limit=10000'

          this.$axios.get(
            requestUrl,
            this.requestConfig
          ).then(
            ({ data }) => {
              resolve(data.result)
            },
            ({ response }) => {
              reject(new APIError('Error fetching coaching plans', response))
            }
          )
        }
      )
    },
    getCoachingContents () {
      return new Promise(
        (resolve, reject) => {
          let requestUrl = process.env.VUE_APP_PROGRAM_URL + '/programPlans?limit=10000'

          this.$axios.get(
            requestUrl,
            this.requestConfig
          ).then(
            ({ data }) => {
              resolve(data.result.result)
            },
            ({ response }) => {
              reject(new APIError('Error fetching coaching contents', response))
            }
          )
        }
      )
    },
    mapUserItemIds (items) {
      return (
        Object.fromEntries(
          Object.entries(items)
            .map(([contentType, contents]) => [contentType, contents.map(content => content.data.id)])
        )
      )
    },
    addDraftContents (payload) {
      let { items, contentType } = payload
      let newDraftContents = items.map(item => new (structs.get(`draft-user-${contentType}`))(item))
      if (contentType === 'action') {
        newDraftContents = (
          newDraftContents.map(
            action => {
              return {
                ...action,
                reminders: [
                  new ActionReminder(
                    {
                      title: action.data.options.reminder.title,
                      body: action.data.options.reminder.body,
                      data: {
                        notification: {
                          buddyId: process.env.VUE_APP_BUDDY_BOT_ID,
                          timestamps: []
                        },
                        isDaily: action.data.options.reminder.isDaily,
                        alarmDate: null,
                        alarmTime: null
                      }
                    }
                  )
                ]
              }
            }
          )
        )
      }

      this.draftContents[contentType] = [...this.draftContents[contentType], ...newDraftContents]
    },
    addDraftContentsViaPlans (payload) {
      let { items: newPlans, contentType } = payload

      let newDraftContents = (
        Array.from(
          new Set(
            newPlans.flatMap(
              plan => plan.data.childPlans.filter(contentId => !this.addedContentIds[contentType].includes(contentId))
            )
          )
        ).map(
          contentId => new (structs.get(`draft-user-${contentType}`))(this.newContents[contentType].find(newContent => newContent.id === contentId))
        )
      )

      this.draftContents[contentType] = [...this.draftContents[contentType], ...newDraftContents]
    },
    updateCoachingContents () {
      this.updatingCoachingContents = true
      this.updateMessage = { class: '', text: '' }

      Promise.all(
        [
          this.postDraftContents(),
          this.postNewUserEvents()
        ]
      ).then(
        () => {
          this.updatingCoachingContents = false
          this.updateMessage = { class: 'success', text: this.$t('patients.coaching.updateSuccess') }
          this.$emit('refresh')
        },
        error => {
          this.updatingCoachingContents = false
          this.updateMessage = { class: 'error', text: this.$t('patients.coaching.errorUpdatingCoachingContents') }
          this.$rollbar.error(error.message, error.response)
        }
      )
    },
    postDraftContents () {
      return new Promise(
        (resolve, reject) => {
          let requestUrl = process.env.VUE_APP_PROGRAM_URL + '/programUsers/' + this.patient.userId + '/userPlans'

          this.$axios.post(
            requestUrl,
            Object.values(this.draftContents).flat(),
            this.requestConfig
          ).then(
            ({ data }) => {
              data.result.updatedPlans
                .filter(content => contentTypes.includes(content.planType))
                .forEach(
                  content => {
                    let type = content.planType
                    let programContentId = content.data.id

                    this.draftContents[type] = (
                      this.draftContents[type].filter(
                        content => content.data.id !== programContentId
                      )
                    )

                    this.addedContents[type].unshift(content)
                  }
                )
              resolve()
            },
            ({ response }) => {
              reject(new APIError('Error posting new coaching contents', response))
            }
          )
        }
      )
    },
    // putAddedContents () {
    //   return new Promise(
    //     (resolve, reject) => {
    //       let requestUrl = process.env.VUE_APP_PROGRAM_URL + '/programUsers/' + this.patient.userId + '/userPlans'
    //
    //       this.$axios.put(
    //         requestUrl,
    //         Object.values(this.addedContents).flat(),
    //         this.requestConfig
    //       ).then(
    //         ({ data }) => {
    //           data.result.updatedPlans
    //             .filter(content => contentTypes.includes(content.planType))
    //             .forEach(
    //               content => {
    //                 let type = content.planType
    //                 let programContentId = content.data.id
    //
    //                 this.addedContents[type] = (
    //                   this.addedContents[type].filter(
    //                     content => content.data.id !== programContentId
    //                   )
    //                 )
    //
    //                 this.addedContents[type].unshift(content)
    //               }
    //             )
    //           resolve()
    //         },
    //         ({ response }) => {
    //           reject(new APIError('Error updating added coaching contents', response))
    //         }
    //       )
    //     }
    //   )
    // },
    putOneAddedContent () {
      return new Promise(
        (resolve, reject) => {
          let requestUrl = process.env.VUE_APP_PROGRAM_URL + '/programUsers/' + this.patient.userId + '/userPlan'

          this.$axios.put(
            requestUrl,
            this.selectedAddedContent,
            this.requestConfig
          ).then(
            ({ data }) => {
              data.result.updatedPlans
                .filter(content => contentTypes.includes(content.planType))
                .forEach(
                  content => {
                    let type = content.planType
                    let programContentId = content.data.id

                    this.addedContents[type] = (
                      this.addedContents[type].filter(
                        content => content.data.id !== programContentId
                      )
                    )

                    this.addedContents[type].unshift(content)
                  }
                )
              resolve()
            },
            ({ response }) => {
              reject(new APIError('Error updating added coaching contents', response))
            }
          )
        }
      )
    },
    postNewUserEvents () {
      return new Promise(
        (resolve, reject) => {
          let requestUrl = process.env.VUE_APP_PROGRAM_URL + '/programUsers/' + this.patient.userId + '/addUserEvents'

          this.$axios.post(
            requestUrl,
            Object.values(this.draftUserEvents),
            this.requestConfig
          ).then(
            ({ data }) => {
              resolve()
            },
            ({ response }) => {
              reject(new APIError('Error posting new user tracking events', response))
            }
          )
        }
      )
    },
    updateSelectedDraftContent (draftContent) {
      let contentId = draftContent.data.id
      let contentType = draftContent.planType
      let selectedDraftContentIndex = this.draftContents[contentType].findIndex(content => content.data.id === contentId)

      this.draftContents[contentType] = (
        [
          ...this.draftContents[contentType].slice(0, selectedDraftContentIndex),
          draftContent,
          ...this.draftContents[contentType].slice(selectedDraftContentIndex + 1)
        ]
      )
    },
    deleteOneDraftContent (draftContent) {
      let contentId = draftContent.data.id
      let contentType = draftContent.planType
      let selectedDraftContentIndex = this.draftContents[contentType].findIndex(content => content.data.id === contentId)

      this.draftContents[contentType] = (
        [
          ...this.draftContents[contentType].slice(0, selectedDraftContentIndex),
          ...this.draftContents[contentType].slice(selectedDraftContentIndex + 1)
        ]
      )
    },
    deactivateSelectedAddedContent () {
      this.contentDeactivationStatus = 'pending'
      this.selectedAddedContent.data.deletedStatus = true
      this.selectedAddedContent.data.deletedTimestamp = Date.now()

      this.putOneAddedContent()
        .then(
          () => {
            this.contentDeactivationStatus = 'success'
          },
          () => {
            this.contentDeactivationStatus = 'failure'
          }
        )
    },
    showDeactivateSelectedAddedContentModal (content) {
      this.contentDeactivationStatus = 'ready'
      this.selectedAddedContent = new UserAction(content)

      this.$nextTick(
        () => {
          $(`#${this.$refs.deactivateContentModal.modalId}`)
            .modal({ closable: false, autofocus: false })
            .modal('show')
        }
      )
    },
    editSelectedDraftContent (content) {
      this.selectedDraftContent = new DraftUserAction(content)

      this.$nextTick(
        () => {
          $(`#${this.$refs.editContentModal.modalId}`)
            .modal({ closable: false, autofocus: false })
            .modal('show')
        }
      )
    }
  }
}
</script>

<style scoped>
.modules-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
}
.horizontal-margin {
  margin: 1rem 0;
}
</style>
